import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ComplianceReport, ReportActivity, ReportRow } from '@regigov/contracts';
import { reportApi } from '../api/compliance';
import { Field, ProblemAlert, StatusBadge, formatDateTime } from './ui';

export const ACTIVITY_LABEL: Record<ReportActivity, string> = {
  monitoring_run: 'Monitoring run',
  case_opened: 'Case opened',
  case_closed: 'Case closed',
  inspection_scheduled: 'Inspection scheduled',
  inspection_completed: 'Inspection completed',
  inspection_cancelled: 'Inspection cancelled',
  audit_planned: 'Audit planned',
  audit_conducted: 'Audit conducted',
  audit_closed: 'Audit closed',
  audit_cancelled: 'Audit cancelled',
  follow_up_completed: 'Follow-up completed',
  enforcement_drafted: 'Enforcement drafted',
  enforcement_issued: 'Enforcement issued',
  enforcement_withdrawn: 'Enforcement withdrawn',
  enforcement_closed: 'Enforcement closed',
};

const sydneyToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' }).format(new Date());
const monthStart = (day: string) => `${day.slice(0, 7)}-01`;
/** '' is the register-wide bucket (no participant). */
type Filter = { activity?: ReportActivity; participant?: string; day?: string };

function recordPath(row: ReportRow): string | null {
  switch (row.record.kind) {
    case 'monitoring_run':
      return `/cer/compliance/monitoring/runs/${row.record.id}`;
    case 'follow_up':
      return row.participantId ? `/cer/compliance/follow-ups?participantId=${row.participantId}` : '/cer/compliance/follow-ups';
    default:
      return row.caseId ? `/cer/compliance/cases/${row.caseId}` : null;
  }
}

/** CER compliance activity report (compliance demo C9): counts by activity, participant and day, drilling to the rows, reconciled to the audit trail. */
export function ReportPage() {
  const [params, setParams] = useSearchParams();
  const today = sydneyToday();
  const from = params.get('from') ?? monthStart(today);
  const to = params.get('to') ?? today;
  const [draft, setDraft] = useState({ from, to });
  const [filter, setFilter] = useState<Filter>({});
  useEffect(() => {
    setDraft({ from, to });
    setFilter({});
  }, [from, to]);
  const report = useQuery({
    queryKey: ['compliance', 'report', from, to],
    queryFn: () => reportApi.view({ from, to }),
    staleTime: 0,
    gcTime: 0,
  });
  const csv = useMutation({
    mutationFn: () => reportApi.downloadCsv({ from, to }),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setFilter({});
    if (draft.from === from && draft.to === to) void report.refetch();
    else setParams({ from: draft.from, to: draft.to });
  };

  const r = report.data;
  return (
    <>
      <div className="row">
        <h1>Compliance activity report</h1>
        <Link to="/cer/compliance/risk">Risk view</Link>
        <Link to="/cer/compliance/cases">Cases</Link>
      </div>
      <p className="notice">
        What the compliance team did in the period, read from the monitoring runs, cases, inspections, audits, follow-ups and enforcement actions themselves. Days are calendar days in Sydney, Australia time and both
        ends are included. Each activity is checked against the audit trail for the same action and record.
      </p>
      <form className="row" onSubmit={onSubmit}>
        <Field label="From">
          <input type="date" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} required />
        </Field>
        <Field label="To">
          <input type="date" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} required />
        </Field>
        <button type="submit" disabled={report.isFetching}>
          {report.isFetching ? 'Running…' : 'Run report'}
        </button>
        <button type="button" className="secondary" disabled={!r || csv.isPending} onClick={() => csv.mutate()}>
          {csv.isPending ? 'Downloading…' : 'Download CSV'}
        </button>
      </form>
      <ProblemAlert error={report.error ?? csv.error} />
      {r && <ReportBody report={r} filter={filter} setFilter={setFilter} />}
    </>
  );
}

function ReportBody({ report: r, filter, setFilter }: { report: ComplianceReport; filter: Filter; setFilter: (f: Filter) => void }) {
  const reconciled = r.reconciliation.every((x) => x.matches) && r.monitoringHits.matches;
  const rows = r.rows.filter(
    (x) => (!filter.activity || x.activity === filter.activity) && (filter.participant === undefined || (x.participantId ?? '') === filter.participant) && (!filter.day || x.day === filter.day),
  );
  const pick = (f: Filter) => setFilter({ ...filter, ...f });
  const active = (on: boolean) => (on ? { fontWeight: 700 } : undefined);
  const participantName = (id: string) => (id ? (r.byParticipant.find((p) => p.participantId === id)?.participantName ?? id) : 'Register-wide');

  return (
    <>
      <p className="muted">
        {r.from} to {r.to} ({r.timeZone}). Generated {formatDateTime(r.generatedAt)}.
      </p>
      <div className="cards">
        <section className="card">
          <h2>{r.total}</h2>
          <p>Activities in the report</p>
        </section>
        <section className="card">
          <h2>{r.auditTotal}</h2>
          <p>Matching audit events</p>
        </section>
        <section className="card">
          <h2>
            <StatusBadge status={reconciled ? 'success' : 'mismatch'} />
          </h2>
          <p>{reconciled ? 'Every activity reconciles to the audit trail.' : 'Some activities do not reconcile; see below.'}</p>
        </section>
        <section className="card">
          <h2>{r.casesByStatus.open + r.casesByStatus.assessing + r.casesByStatus.acting}</h2>
          <p>
            Cases not closed now: {r.casesByStatus.open} open · {r.casesByStatus.assessing} assessing · {r.casesByStatus.acting} acting · {r.casesByStatus.closed} closed
          </p>
        </section>
      </div>

      <div className="cards">
        <section className="card">
          <h2>By activity</h2>
          <table className="table">
            <tbody>
              {r.byActivity.map((a) => (
                <tr key={a.activity}>
                  <td>
                    <button type="button" className="linklike" style={active(filter.activity === a.activity)} onClick={() => pick({ activity: a.activity })}>
                      {ACTIVITY_LABEL[a.activity]}
                    </button>
                    {a.subtypes.length > 0 && <div className="muted">{a.subtypes.map((s) => `${s.subtype.replace(/_/g, ' ')} ${s.count}`).join(' · ')}</div>}
                  </td>
                  <td>{a.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <section className="card">
          <h2>By participant</h2>
          <table className="table">
            <tbody>
              {r.byParticipant.map((p) => (
                <tr key={p.participantId ?? ''}>
                  <td>
                    <button type="button" className="linklike" style={active(filter.participant === (p.participantId ?? ''))} onClick={() => pick({ participant: p.participantId ?? '' })}>
                      {participantName(p.participantId ?? '')}
                    </button>
                  </td>
                  <td>{p.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <section className="card">
          <h2>By day</h2>
          <table className="table">
            <tbody>
              {r.byDay.map((d) => (
                <tr key={d.day}>
                  <td>
                    <button type="button" className="linklike" style={active(filter.day === d.day)} onClick={() => pick({ day: d.day })}>
                      {d.day}
                    </button>
                  </td>
                  <td>{d.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      <h2>Reconciliation to the audit trail</h2>
      <table className="table">
        <thead>
          <tr>
            <th>Activity</th>
            <th>Audit action</th>
            <th>Report</th>
            <th>Audit events</th>
            <th>Result</th>
          </tr>
        </thead>
        <tbody>
          {r.reconciliation.map((x) => (
            <tr key={x.activity}>
              <td>{ACTIVITY_LABEL[x.activity]}</td>
              <td>
                <code>{x.auditAction}</code>
              </td>
              <td>{x.reportCount}</td>
              <td>{x.auditCount}</td>
              <td>
                <StatusBadge status={x.matches ? 'success' : 'mismatch'} />
                {x.missingAudit.length > 0 && <div className="error">No audit event for: {x.missingAudit.join(', ')}</div>}
                {x.unmatchedAudit.length > 0 && (
                  <div className="error">Audit events with no record in the period: {x.unmatchedAudit.map((e) => `#${e.auditEventId} (${e.entityId ?? 'no entity'})`).join(', ')}</div>
                )}
              </td>
            </tr>
          ))}
          <tr>
            <td>Monitoring hits</td>
            <td>
              <code>detail.hitCount</code>
            </td>
            <td>{r.monitoringHits.reportCount}</td>
            <td>{r.monitoringHits.auditCount}</td>
            <td>
              <StatusBadge status={r.monitoringHits.matches ? 'success' : 'mismatch'} />
            </td>
          </tr>
        </tbody>
      </table>

      <div className="row">
        <h2>
          Rows ({rows.length} of {r.total})
        </h2>
        {(filter.activity || filter.participant !== undefined || filter.day) && (
          <>
            <span className="muted">
              {[filter.activity && ACTIVITY_LABEL[filter.activity], filter.participant !== undefined && participantName(filter.participant), filter.day].filter(Boolean).join(' · ')}
            </span>
            <button type="button" className="secondary" onClick={() => setFilter({})}>
              Clear filter
            </button>
          </>
        )}
      </div>
      {rows.length === 0 ? (
        <p>No compliance activity in the period.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>When</th>
              <th>Activity</th>
              <th>Participant</th>
              <th>Record</th>
              <th>Case</th>
              <th>By</th>
              <th>Audit event</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((x) => {
              const path = recordPath(x);
              return (
                <tr key={`${x.activity}:${x.record.id}`}>
                  <td>{formatDateTime(x.occurredAt)}</td>
                  <td>
                    {ACTIVITY_LABEL[x.activity]}
                    {x.subtype && <div className="muted">{x.subtype.replace(/_/g, ' ')}</div>}
                  </td>
                  <td>{x.participantName ?? (x.participantId ? x.participantId : <span className="muted">Register-wide</span>)}</td>
                  <td>
                    {path ? <Link to={path}>{x.record.label}</Link> : x.record.label}
                    <div className="muted">
                      <code>{x.record.id}</code>
                    </div>
                  </td>
                  <td>{x.caseId ? <Link to={`/cer/compliance/cases/${x.caseId}`}>{x.caseId}</Link> : '—'}</td>
                  <td>{x.actorName ?? <span className="muted">Not recorded</span>}</td>
                  <td>{x.auditEventId !== null ? <code>#{x.auditEventId}</code> : <span className="error">None</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}
